import { OptionallyVisible } from 'components';
import { useEffect, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { useGetMerchantOnboardingTokenQuery } from 'services/merchants';
import { BridgeKycInfoData, MerchantInfo, MerchantPayoutFlow } from 'services/merchants/types';

import { Panel } from 'components/containers';
import { SelectItem } from 'components/inputs/select/select';

import { ModalStateProps } from 'pages/merchant/route';

import { Response, SuccessResponse } from 'types/http';

import { TitanModal } from './components/modal/components/titanModal/titanModal';
import { MerchantKybModal } from './components/modal/merchantKybModal';
import { FormType, KEYS, LABELS, ONBOARDING_LABELS } from './keys';
import { OnboardingPanel } from './onboarding-panel/onboarding-panel';
import { KybPanel } from './panel/panel';
import { FormContainer } from './styles';
import { UseSessionFormResult } from './useSessionForm';

export interface AutoKybProps {
  run: () => Promise<void>;
  isProcessing?: boolean;
}

interface GeneralSectionProps extends WithTranslation, ModalStateProps {
  merchant: MerchantInfo;
  countries: SelectItem[];
  //  for errors coming from backend
  error?: Record<string, string>;
  onConfirm: (merchantKybPayload: Partial<FormType>) => Promise<void>;
  isUpdatingMerchantKyb: boolean;
  merchantKybData?: Response<SuccessResponse>;
  clearBackendErrors?: () => void;
  resetMutation: () => void;
  autoKybProps: AutoKybProps;
  titanFormData?: FormType;
  isLoadingTitanData?: boolean;
  fetchTitanData?: () => Promise<void>;
  openAssociatedPersonModal?: () => void;
  sessionControllers: UseSessionFormResult;
  initialFormValues?: FormType;
  shouldShowPanel: boolean;
  bridgeKycInfo?: BridgeKycInfoData | null;
}

export const KybComponent = ({
  t,
  merchant,
  countries,
  error,
  onConfirm,
  isUpdatingMerchantKyb,
  merchantKybData,
  clearBackendErrors,
  resetMutation,
  autoKybProps,
  titanFormData,
  isLoadingTitanData,
  fetchTitanData,
  openAssociatedPersonModal,
  sessionControllers,
  initialFormValues,
  visibleModal,
  setVisibleModal,
  shouldShowPanel,
  bridgeKycInfo,
}: GeneralSectionProps) => {
  const { refreshSessionValues, setFormValues } = sessionControllers;
  const { data: onboardingTokenData, isLoading: isLoadingToken } = useGetMerchantOnboardingTokenQuery(merchant.uuid);

  // Keep the panel mounted once a token has been observed so that the
  // "all accepted" completed state stays visible after the token is cleared.
  const [hadToken, setHadToken] = useState(false);
  useEffect(() => {
    if (onboardingTokenData?.onboardingToken) {
      setHadToken(true);
    }
  }, [onboardingTokenData?.onboardingToken]);

  const handleModalOpen = async () => {
    clearBackendErrors?.();
    resetMutation();
    refreshSessionValues();
    await fetchTitanData?.();
    setVisibleModal(titanFormData ? KEYS.TITAN : KEYS.KYB);
  };

  const handleModalClose = () => {
    setVisibleModal(null);
  };

  const handleTitanModalAccept = () => {
    setFormValues({ uuid: merchant.uuid, ...initialFormValues, ...titanFormData! });
    setVisibleModal(KEYS.KYB);
  };

  const handleInsertTitanData = () => {
    setFormValues({ uuid: merchant.uuid, ...initialFormValues, ...titanFormData! });
  };

  const handleTitanModalDecline = () => {
    setVisibleModal(KEYS.KYB);
  };

  return (
    <>
      <OptionallyVisible visible={shouldShowPanel}>
        <Panel label={t(LABELS.PANEL_TITLE)}>
          <FormContainer>
            <KybPanel
              merchant={merchant}
              isKyb={merchant.payoutFlow === MerchantPayoutFlow.SETTLEMENT_WALLET}
              openModal={handleModalOpen}
              openAssociatedPersonModal={openAssociatedPersonModal ?? (() => {})}
              autoKybProps={autoKybProps}
              titanFormData={titanFormData}
              isLoadingTitanData={isLoadingTitanData}
            />
          </FormContainer>
        </Panel>
      </OptionallyVisible>
      <OptionallyVisible visible={!isLoadingToken && (Boolean(onboardingTokenData?.onboardingToken) || hadToken)}>
        <Panel label={t(ONBOARDING_LABELS.PANEL_TITLE)}>
          <FormContainer>
            <OnboardingPanel
              onboardingToken={onboardingTokenData?.onboardingToken ?? null}
              allTosAccepted={!onboardingTokenData?.onboardingToken}
            />
          </FormContainer>
        </Panel>
      </OptionallyVisible>
      <MerchantKybModal
        error={error}
        countries={countries}
        merchant={merchant}
        closeModal={handleModalClose}
        onConfirm={onConfirm}
        isOpen={visibleModal === KEYS.KYB}
        isUpdatingMerchantKyb={isUpdatingMerchantKyb}
        merchantKybData={merchantKybData}
        clearBackendErrors={clearBackendErrors}
        sessionControllers={sessionControllers}
        onInsertTitanData={handleInsertTitanData}
        titanFormData={titanFormData}
        isLoadingTitanData={isLoadingTitanData}
        bridgeKycInfo={bridgeKycInfo}
      />
      <TitanModal
        isOpen={visibleModal === KEYS.TITAN}
        onClose={handleModalClose}
        onAccept={handleTitanModalAccept}
        onDecline={handleTitanModalDecline}
      />
    </>
  );
};

export const KYB = withTranslation()(KybComponent);
